/**
 * Overlay Gesture Hook
 *
 * Handles pointer events for drag/resize/rotate gestures.
 * Uses pointer capture and delta-based calculations.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { OverlayElement } from '../types';
import { clamp, pxToPct, normalizeDeg, angleBetweenPoints, shortestDeltaDeg, distance } from './overlay-math';

type GestureMode = 'idle' | 'drag' | 'resize' | 'rotate';

interface GestureState {
  mode: GestureMode;
  overlayId: string;
  startPointer: { x: number; y: number };
  startTransform: OverlayElement['transform'];
  stageRect: DOMRect;
  startPointerAngle?: number;
  startDistance?: number;
  overlayCenter?: { x: number; y: number };
}

interface UseOverlayGestureOptions {
  stageRef: React.RefObject<HTMLDivElement | null>;
  onUpdate: (id: string, transform: OverlayElement['transform']) => void;
}

export function useOverlayGesture({ stageRef, onUpdate }: UseOverlayGestureOptions) {
  const [gesture, setGesture] = useState<GestureState | null>(null);
  const [draftTransform, setDraftTransform] = useState<OverlayElement['transform'] | null>(null);
  const pointerDataRef = useRef<{ target: HTMLElement; pointerId: number } | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pointerDataRef.current) {
        try {
          pointerDataRef.current.target.releasePointerCapture(pointerDataRef.current.pointerId);
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const handlePointerDown = useCallback(
    (
      e: React.PointerEvent,
      overlayId: string,
      overlay: OverlayElement,
      mode: 'drag' | 'resize' | 'rotate',
      overlayCenter?: { x: number; y: number }
    ) => {
      // Ignore non-primary button (right-click / middle-click)
      if (e.button !== 0) return;
      // Race guard: state still active
      if (gesture) return;
      // Race guard: ref still active
      if (pointerDataRef.current) return;

      e.stopPropagation();
      if (!stageRef.current) return;

      const stageRect = stageRef.current.getBoundingClientRect();
      const pointer = { x: e.clientX, y: e.clientY };

      // Capture pointer
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);
      pointerDataRef.current = { target, pointerId: e.pointerId };

      let startPointerAngle: number | undefined;
      let startDistance: number | undefined;

      if (mode === 'rotate' && overlayCenter) {
        startPointerAngle = angleBetweenPoints(
          overlayCenter.x,
          overlayCenter.y,
          pointer.x,
          pointer.y
        );
      } else if (mode === 'resize' && overlayCenter) {
        const dist = distance(overlayCenter, pointer);
        // Guard against zero distance (minimum 1px to prevent division by zero)
        startDistance = Math.max(dist, 1);
      }

      setGesture({
        mode,
        overlayId,
        startPointer: pointer,
        startTransform: { ...overlay.transform },
        stageRect,
        startPointerAngle,
        startDistance,
        overlayCenter,
      });
    },
    [stageRef]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent | PointerEvent) => {
      if (!gesture || !stageRef.current) return;

      const activePointerId = pointerDataRef.current?.pointerId;
      if (typeof activePointerId === 'number' && e.pointerId !== activePointerId) return;

      const pointer = { x: e.clientX, y: e.clientY };
      const { mode, startPointer, startTransform, stageRect, overlayCenter } = gesture;

      const newTransform: OverlayElement['transform'] = { ...startTransform };

      if (mode === 'drag') {
        const dx = pointer.x - startPointer.x;
        const dy = pointer.y - startPointer.y;
        const dxPct = pxToPct(dx, stageRect.width);
        const dyPct = pxToPct(dy, stageRect.height);
        newTransform.xPct = clamp(startTransform.xPct + dxPct, 0, 100);
        newTransform.yPct = clamp(startTransform.yPct + dyPct, 0, 100);
      } else if (mode === 'resize' && overlayCenter && gesture.startDistance && gesture.startDistance > 0) {
        const currentDistance = distance(overlayCenter, pointer);
        const scaleRatio = currentDistance / gesture.startDistance;
        newTransform.scale = clamp(startTransform.scale * scaleRatio, 0.1, 10);
      } else if (mode === 'rotate' && overlayCenter && gesture.startPointerAngle !== undefined) {
        const currentAngle = angleBetweenPoints(
          overlayCenter.x,
          overlayCenter.y,
          pointer.x,
          pointer.y
        );
        const deltaAngle = shortestDeltaDeg(currentAngle, gesture.startPointerAngle);
        newTransform.rotationDeg = normalizeDeg(startTransform.rotationDeg + deltaAngle);
      }

      setDraftTransform(newTransform);
    },
    [gesture, stageRef]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent | PointerEvent) => {
      // Must have an active gesture
      if (!gesture) return;

      // Ignore unrelated pointers
      const activePointerId = pointerDataRef.current?.pointerId;
      if (typeof activePointerId === 'number' && e.pointerId !== activePointerId) return;

      // Release pointer capture
      if (pointerDataRef.current) {
        try {
          pointerDataRef.current.target.releasePointerCapture(pointerDataRef.current.pointerId);
        } catch {
          // ignore
        }
        pointerDataRef.current = null;
      }

      // Commit only if there was a transform change
      if (draftTransform) {
        onUpdate(gesture.overlayId, draftTransform);
      }

      // Always clear gesture state
      setGesture(null);
      setDraftTransform(null);
    },
    [gesture, draftTransform, onUpdate]
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent | PointerEvent) => {
      if (!gesture) return;

      const activePointerId = pointerDataRef.current?.pointerId;
      if (typeof activePointerId === 'number' && e.pointerId !== activePointerId) return;

      // Use stored pointer data instead of e.currentTarget (which is window for global events)
      if (pointerDataRef.current) {
        try {
          pointerDataRef.current.target.releasePointerCapture(pointerDataRef.current.pointerId);
        } catch {
          // ignore
        }
        pointerDataRef.current = null;
      }

      // Cancel gesture (don't commit)
      setGesture(null);
      setDraftTransform(null);
    },
    [gesture]
  );

  return {
    gesture,
    draftTransform,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
  };
}
